import React, { useState, useEffect, useRef } from "react";
import NextButton from "../Component/NextButton";
import BackButton from "../Component/BackButton";
import { Undo2, Mic, Square, Volume2 } from "lucide-react";
import CartChatpterComp from "./CartChatpterComp";
import axios from "axios";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import useNumberSpeech from "../hooks/useNumberSpeech";

function Answer_question({ answer_question, picture }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [results, setResults] = useState({});
  const [isWaiting, setIsWaiting] = useState(false);
  const [value, setValue] = useState(false);
  const [userAnswerQuestion, setUserAnswerQuestion] = useState([]);
  const [userText, setUserText] = useState("");
  const [status, setStatus] = useState(false);
  const [tab, setTab] = useState(0);
  const [listeningState, setListeningState] = useState("idle"); // idle, recording, analyzing, success, error
  // Hook'tan normalizeText fonksiyonunu çekiyoruz
  const { normalizeText } = useNumberSpeech();
  // Web Speech API için gerekli state'ler
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [error, setError] = useState(null);

  const currentItem = answer_question[currentIndex];

  const [userAllContent, setUserAllContent] = useState({
    userId: "",
    name: "",
    class_name: "",
    answer_question: null,
  });

  const contentValue = useSelector((state) => state.extra.value);
  const user = JSON.parse(localStorage.getItem("postUser"));
  const navigate = useNavigate();

  useEffect(() => {
    if (currentIndex === answer_question.length) {
      setUserAllContent({
        class_name: contentValue.class_name,
        name: contentValue.name,
        answer_question: userAnswerQuestion,
        userId: user?._id,
      });
      setValue(true);
      setTab(3);
    }
  }, [currentIndex, answer_question.length]);

  // Web Speech API Tanımlaması (En üst seviyede useEffect içinde)
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError(
        "Your browser does not support Speech Recognition. Please use Google Chrome or Microsoft Edge.",
      );
      return;
    }

    const recognitionInstance = new SpeechRecognition();
    recognitionInstance.continuous = false;
    recognitionInstance.interimResults = false;
    recognitionInstance.lang = "en-US";

    recognitionInstance.onresult = (event) => {
      const rawSpokentext = event.results[0][0].transcript;
      const spokenText = normalizeText(rawSpokentext);
      const isCorrect = currentItem?.answers?.some(
        (ans) =>
          spokenText.toLowerCase().includes(ans.toLowerCase().trim()) ||
          ans.toLowerCase().trim().includes(spokenText.toLowerCase()),
      );

      setUserAnswers((prev) => ({ ...prev, [currentIndex]: spokenText }));
      setStatus(isCorrect);
      setResults((prev) => ({
        ...prev,
        [currentIndex]: isCorrect ? "correct" : "wrong",
      }));
      setListeningState(isCorrect ? "success" : "error");

      if (isCorrect) {
        setUserText(`✓ Correct! "${spokenText}"`);
      } else {
        setUserText(`✗ Try again! "${spokenText}"`);
      }

      setUserAnswerQuestion((prev) => [
        ...prev,
        {
          answerId: currentItem?._id,
          givenAnswer: spokenText,
          answers: currentItem?.answers,
          question: currentItem?.question,
          status: isCorrect,
        },
      ]);

      setIsWaiting(true);
      setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
        setIsWaiting(false);
        setListeningState("idle");
        setUserText("");
      }, 2500);
    };

    recognitionInstance.onerror = (event) => {
      setError(`Error occurred in recognition: ${event.error}`);
      setIsListening(false);
      setListeningState("error");
    };

    recognitionInstance.onend = () => {
      setIsListening(false);
    };

    setRecognition(recognitionInstance);

    return () => {
      if (recognitionInstance) {
        recognitionInstance.abort();
      }
    };
  }, [currentIndex, currentItem]);

  // Temizlik işlemleri
  useEffect(() => {
    return () => {
      if (recognition && isListening) {
        recognition.stop();
      }
    };
  }, [recognition, isListening]);

  // Soruyu Seslendir
  const askQuestion = (text) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    utterance.rate = 0.8;
    window.speechSynthesis.speak(utterance);
  };

  // Dinlemeyi başlatma fonksiyonu
  const startListening = () => {
    if (!recognition) return;

    if (isListening) {
      recognition.stop();
      setIsListening(false);
      setListeningState("idle");
      return;
    }

    setListeningState("recording");
    setUserText("");
    setStatus(null);
    setError(null);

    try {
      recognition.start();
      setIsListening(true);
    } catch (err) {
      console.error("Recognition start error:", err);
      setIsListening(false);
      setListeningState("error");
    }
  };

  const handleNextButton = () => {
    if (currentIndex < answer_question.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setListeningState("idle");
      setUserText("");
    }
  };

  const handleBackButton = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setListeningState("idle");
      setUserText("");
    }
  };

  if (tab === 3) {
    return (
      <CartChatpterComp
        value={value}
        status="answer_question"
        userAllContent={userAllContent}
      />
    );
  }

  if (currentIndex >= answer_question.length) {
    return null;
  }

  const isRecordingOrAnalyzing =
    listeningState === "recording" || listeningState === "analyzing";

  const getDynamicConfig = () => {
    switch (listeningState) {
      case "recording":
        return {
          centerStyle: "bg-red-500 border-red-600 text-white animate-pulse",
          centerText: "🎙️ Recording... Speak now!",
          centerSub: "YOUR SPEECH IS BEING RECORDED",
          speakBtnColor: "bg-red-600 border-red-700 shadow-[0_4px_0_#991b1b]",
        };
      case "analyzing":
        return {
          centerStyle: "bg-purple-600 border-yellow-700 text-white",
          centerText: "🤔 Analyzing your answer...",
          centerSub: "AI IS CHECKING",
          speakBtnColor:
            "bg-purple-700 border-purple-800 shadow-[0_4px_0_#4c1d95]",
        };
      case "success":
        return {
          centerStyle: "bg-green-500 border-green-600 text-white font-black",
          centerText: userText || "✓ Excellent! ⭐",
          centerSub: "RESULT: CORRECT",
          speakBtnColor:
            "bg-green-600 border-green-700 shadow-[0_4px_0_#15803d]",
        };
      case "error":
        return {
          centerStyle: "bg-red-500 border-red-600 text-white font-bold",
          centerText: userText || "❌ Try Again! 💪",
          centerSub: "RESULT: WRONG / NOT UNDERSTOOD",
          speakBtnColor: "bg-red-600 border-red-700 shadow-[0_4px_0_#991b1b]",
        };
      default:
        return {
          centerStyle: "bg-white border-yellow-500 text-purple-950",
          centerText: currentItem?.question || "",
          centerSub: "QUESTION",
          speakBtnColor:
            "bg-purple-600 hover:bg-purple-700 border-purple-700 active:translate-y-[2px] active:shadow-[0_2px_0_#5b21b6] shadow-[0_4px_0_#5b21b6]",
        };
    }
  };

  const dynamicConfig = getDynamicConfig();

  return (
    <div
      className="flex flex-col w-full gap-4 max-w-5xl mx-auto rounded-xl overflow-hidden"
      style={{ fontFamily: "Inter, system-ui, -apple-system, sans-serif" }}
    >
      <div className="grid grid-cols-4 p-2 items-center w-full gap-4 bg-yellow-500 rounded-xl">
        <div className="col-span-3 md:col-span-3 flex items-center">
          <span className="font-medium rounded-xl text-white text-md p-2">
            Answer Question
          </span>
        </div>
        <div className="col-span-1 md:col-span-1 flex justify-end items-center">
          <button
            onClick={() => navigate("/extraPage")}
            className="bg-gray-100 h-10 w-10 text-red-500 hover:bg-red-500 hover:text-gray-100 transition-all rounded-full flex items-center justify-center shadow-lg active:scale-90 shrink-0"
          >
            <Undo2 size={20} />
          </button>
        </div>
      </div>

      <div className="flex flex-col w-full bg-purple-500 gap-4 max-w-5xl mx-auto rounded-xl shadow-2xl overflow-hidden">
        <div className="grid grid-cols-12 p-2 justify-center items-center w-full gap-2 md:gap-4">
          <div
            className={`col-span-2 sm:col-span-1 flex justify-start items-center ${
              currentIndex === 0 || isRecordingOrAnalyzing
                ? "opacity-30 pointer-events-none"
                : ""
            }`}
          >
            <BackButton handleBackButton={handleBackButton} />
          </div>
          <div className="col-span-8 sm:col-span-10 flex justify-center items-center">
            <div className="flex flex-wrap justify-center items-center gap-2 p-2 rounded-2xl">
              {answer_question.map((_, i) => (
                <div
                  key={i}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    i === currentIndex
                      ? "bg-yellow-400 scale-150 shadow-md"
                      : results[i] === "correct"
                        ? "bg-green-400"
                        : results[i] === "wrong"
                          ? "bg-red-400"
                          : "bg-purple-300"
                  }`}
                />
              ))}
            </div>
          </div>
          <div
            className={`col-span-2 sm:col-span-1 flex justify-end items-center ${
              currentIndex === answer_question.length - 1 ||
              isRecordingOrAnalyzing
                ? "opacity-30 pointer-events-none"
                : ""
            }`}
          >
            <NextButton handleNextButton={handleNextButton} />
          </div>
        </div>

        <div className="flex flex-row items-center justify-between gap-4">
          <div className="flex-1 flex flex-col items-center gap-4">
            <div className="w-full max-w-2xl rounded-2xl shadow-inner relative border-l-6 border-yellow-500">
              <img
                src={picture}
                alt="Exercise"
                className="w-full h-full rounded-xl aspect-video"
              />
            </div>

            {/* Desktop / Tablet View */}
            <div className="w-full max-w-2xl lg:grid grid-cols-12 rounded-xl p-4 gap-3 items-stretch min-h-[140px] hidden">
              <button
                disabled={isRecordingOrAnalyzing}
                onClick={() => askQuestion(currentItem.question)}
                className="col-span-3 flex flex-col justify-center items-center gap-1 rounded-xl py-5 bg-yellow-500 hover:bg-yellow-400 text-white transition-all duration-150 h-full cursor-pointer select-none disabled:opacity-40 disabled:pointer-events-none"
              >
                <Volume2 size={28} />
                <span className="text-xs font-black uppercase tracking-wider mt-1">
                  Listen
                </span>
              </button>

              <div
                className={`col-span-6 p-3 rounded-xl flex items-center justify-start text-left transition-all duration-300 shadow-inner border-l-6 border-yellow-500 h-full overflow-hidden ${dynamicConfig.centerStyle}`}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex flex-col w-full min-w-0">
                    <span className="text-[8px] font-bold uppercase tracking-widest opacity-60 block mb-0.5">
                      {dynamicConfig.centerSub}
                    </span>
                    <h2 className="text-xs md:text-sm font-black leading-tight line-clamp-3">
                      {dynamicConfig.centerText}
                    </h2>
                  </div>
                </div>
              </div>

              <button
                disabled={isWaiting}
                onClick={startListening}
                className="col-span-3 flex flex-col justify-center items-center gap-1 rounded-xl py-5 bg-yellow-500 hover:bg-yellow-400 text-white transition-all duration-150 h-full cursor-pointer select-none disabled:opacity-40 disabled:pointer-events-none"
              >
                {listeningState === "recording" ? (
                  <Square size={26} className="animate-pulse" />
                ) : listeningState === "analyzing" ? (
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Mic size={28} />
                )}
                <span className="text-xs font-black uppercase tracking-wider mt-1">
                  {listeningState === "recording" ? "Stop" : "Speak"}
                </span>
              </button>
            </div>

            {/* Mobile View */}
            <div className="w-full max-w-2xl grid grid-rows-2 rounded-xl p-4 gap-3 items-stretch min-h-[140px] lg:hidden">
              <div
                className={`row-span-1 p-3 rounded-xl flex items-center justify-start text-left transition-all duration-300 shadow-inner border-l-6 border-yellow-500 h-full overflow-hidden ${dynamicConfig.centerStyle}`}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex flex-col w-full min-w-0">
                    <span className="text-[8px] font-bold uppercase tracking-widest opacity-60 block mb-0.5">
                      {dynamicConfig.centerSub}
                    </span>
                    <h2 className="text-xs md:text-sm font-black leading-tight line-clamp-3">
                      {dynamicConfig.centerText}
                    </h2>
                  </div>
                </div>
              </div>

              <div className="row-span-1 flex justify-between items-center gap-2">
                <button
                  disabled={isRecordingOrAnalyzing}
                  onClick={() => askQuestion(currentItem.question)}
                  className="flex flex-col justify-center w-full items-center gap-1 rounded-xl py-5 bg-yellow-500 hover:bg-yellow-400 text-white transition-all duration-150 h-full cursor-pointer select-none disabled:opacity-40 disabled:pointer-events-none"
                >
                  <Volume2 size={28} />
                  <span className="text-xs font-black uppercase tracking-wider mt-1">
                    Listen
                  </span>
                </button>

                <button
                  disabled={isWaiting}
                  onClick={startListening}
                  className="w-full flex flex-col justify-center items-center gap-1 rounded-xl py-5 bg-yellow-500 hover:bg-yellow-400 text-white transition-all duration-150 h-full cursor-pointer select-none disabled:opacity-40 disabled:pointer-events-none"
                >
                  {listeningState === "recording" ? (
                    <Square size={26} className="animate-pulse" />
                  ) : listeningState === "analyzing" ? (
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Mic size={28} />
                  )}
                  <span className="text-xs font-black uppercase tracking-wider mt-1">
                    {listeningState === "recording" ? "Stop" : "Speak"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Answer_question;
